// src/pages/AdminPanel.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Tabs, Tab, Box, Card, CardContent, Typography, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, IconButton, Chip,
  Alert, CircularProgress, Paper, Grid, Switch, FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Assessment as AssessmentIcon,
  HelpOutline as QuestionIcon,
  Dashboard as DashboardIcon,
  Restore as RestoreIcon,
  Lock as LockIcon,
  Description as DescriptionIcon,
  Psychology as AiIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const MODULES = [
  { key: 'assessments', label: 'Assessments' },
  { key: 'vulnerability-scans', label: 'Vulnerability Scans' },
  { key: 'penetration-tests', label: 'Penetration Tests' },
  { key: 'ad-audits', label: 'AD Audits' },
  { key: 'value-of-risk', label: 'Value of Risk' },
  { key: 'nar', label: 'NAR' },
];

const DEFAULT_MODULE_PERMS = Object.fromEntries(MODULES.map(m => [m.key, 'user']));

const AdminPanel = () => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dashboard Stats
  const [stats, setStats] = useState(null);

  // Users Management
  const [users, setUsers] = useState([]);
  const [userDialog, setUserDialog] = useState({ open: false, user: null });
  const [userForm, setUserForm] = useState({ username: '', email: '', password: '', full_name: '', role: 'assessor', auth_method: 'entra' });
  const [modulePerms, setModulePerms] = useState(DEFAULT_MODULE_PERMS);

  // Assessment Types Management
  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [typeDialog, setTypeDialog] = useState({ open: false, type: null });
  const [typeForm, setTypeForm] = useState({ name: '', code: '', description: '', version: '' });

  // IP Allowlist Management
  const [allowedIps, setAllowedIps] = useState([]);
  const [ipDialog, setIpDialog] = useState({ open: false, ip: null });
  const [ipForm, setIpForm] = useState({ ip_address: '', description: '', is_active: true });

  // Questions Management
  const [questions, setQuestions] = useState([]);
  const [selectedAssessmentType, setSelectedAssessmentType] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [sections, setSections] = useState([]);
  const [questionDialog, setQuestionDialog] = useState({ open: false, question: null });
  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    guidance: '',
    subcategory_code: '',
    weight: 1.0,
    impact_reduction: 0,
    likelihood_reduction: 0,
    is_required: true
  });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
  });

  // ==================== Dashboard ====================
  
  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/admin/stats`, getAuthHeaders());
      setStats(response.data);
    } catch (err) {
      setError('Failed to load dashboard stats');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ==================== Users Management ====================

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/admin/users`, getAuthHeaders());
      setUsers(response.data);
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const saveModulePerms = async (userId) => {
    await axios.put(`${API_URL}/admin/users/${userId}/modules`, modulePerms, getAuthHeaders());
  };

  const handleCreateUser = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${API_URL}/admin/users`, userForm, getAuthHeaders());
      const newUserId = res.data?.id;
      if (newUserId && userForm.role !== 'admin') {
        await saveModulePerms(newUserId);
      }
      setSuccess('User created successfully');
      setUserDialog({ open: false, user: null });
      setUserForm({ username: '', email: '', password: '', full_name: '', role: 'assessor', auth_method: 'entra' });
      setModulePerms(DEFAULT_MODULE_PERMS);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    try {
      setLoading(true);
      await axios.put(`${API_URL}/admin/users/${userDialog.user.id}`, userForm, getAuthHeaders());
      if (userForm.role !== 'admin') {
        await saveModulePerms(userDialog.user.id);
      }
      setSuccess('User updated successfully');
      setUserDialog({ open: false, user: null });
      setUserForm({ username: '', email: '', password: '', full_name: '', role: 'assessor', auth_method: 'entra' });
      setModulePerms(DEFAULT_MODULE_PERMS);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    
    try {
      setLoading(true);
      await axios.delete(`${API_URL}/admin/users/${userId}`, getAuthHeaders());
      setSuccess('User deactivated successfully');
      loadUsers();
    } catch (err) {
      setError('Failed to deactivate user');
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateUser = async (userId) => {
    if (!window.confirm('Are you sure you want to reactivate this user?')) return;
    
    try {
      setLoading(true);
      await axios.post(`${API_URL}/admin/users/${userId}/reactivate`, {}, getAuthHeaders());
      setSuccess('User reactivated successfully');
      loadUsers();
    } catch (err) {
      setError('Failed to reactivate user');
    } finally {
      setLoading(false);
    }
  };

  const openUserDialog = async (user = null) => {
    if (user) {
      setUserForm({
        username: user.username,
        email: user.email,
        password: '',
        full_name: user.full_name || '',
        role: user.role,
        auth_method: user.auth_method || 'entra',
      });
      try {
        const res = await axios.get(`${API_URL}/admin/users/${user.id}/modules`, getAuthHeaders());
        setModulePerms({ ...DEFAULT_MODULE_PERMS, ...res.data });
      } catch {
        setModulePerms(DEFAULT_MODULE_PERMS);
      }
    } else {
      setUserForm({ username: '', email: '', password: '', full_name: '', role: 'assessor', auth_method: 'entra' });
      setModulePerms(DEFAULT_MODULE_PERMS);
    }
    setUserDialog({ open: true, user });
  };

  // ==================== IP Allowlist Management ====================

  const loadAllowedIps = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/admin/allowed-ips`, getAuthHeaders());
      setAllowedIps(response.data);
    } catch (err) {
      setError('Failed to load allowed IPs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIp = async () => {
    try {
      setLoading(true);
      await axios.post(`${API_URL}/admin/allowed-ips`, ipForm, getAuthHeaders());
      setSuccess('IP added to allowlist');
      setIpDialog({ open: false, ip: null });
      setIpForm({ ip_address: '', description: '', is_active: true });
      loadAllowedIps();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add IP');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateIp = async () => {
    try {
      setLoading(true);
      await axios.put(`${API_URL}/admin/allowed-ips/${ipDialog.ip.id}`, ipForm, getAuthHeaders());
      setSuccess('IP updated');
      setIpDialog({ open: false, ip: null });
      setIpForm({ ip_address: '', description: '', is_active: true });
      loadAllowedIps();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update IP');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteIp = async (ipId) => {
    if (!window.confirm('Remove this IP from the allowlist?')) return;
    try {
      setLoading(true);
      await axios.delete(`${API_URL}/admin/allowed-ips/${ipId}`, getAuthHeaders());
      setSuccess('IP removed from allowlist');
      loadAllowedIps();
    } catch (err) {
      setError('Failed to remove IP');
    } finally {
      setLoading(false);
    }
  };

  const openIpDialog = (ip = null) => {
    if (ip) {
      setIpForm({ ip_address: ip.ip_address, description: ip.description || '', is_active: ip.is_active });
    } else {
      setIpForm({ ip_address: '', description: '', is_active: true });
    }
    setIpDialog({ open: true, ip });
  };

  // ==================== Assessment Types Management ====================

  const loadAssessmentTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/assessment-types?active=false`, getAuthHeaders());
      setAssessmentTypes(response.data.assessment_types);
    } catch (err) {
      setError('Failed to load assessment types');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAssessmentType = async () => {
    try {
      setLoading(true);
      await axios.post(`${API_URL}/admin/assessment-types`, typeForm, getAuthHeaders());
      setSuccess('Assessment type created successfully');
      setTypeDialog({ open: false, type: null });
      setTypeForm({ name: '', code: '', description: '', version: '' });
      loadAssessmentTypes();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create assessment type');
    } finally {
      setLoading(false);
    }
  };

  const openTypeDialog = (type = null) => {
    if (type) {
      setTypeForm({
        name: type.name,
        code: type.code,
        description: type.description || '',
        version: type.version || ''
      });
    } else {
      setTypeForm({ name: '', code: '', description: '', version: '' });
    }
    setTypeDialog({ open: true, type });
  };

  // ==================== Questions Management ====================

  const loadQuestions = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/questions?`;
      if (selectedAssessmentType) url += `assessment_type_id=${selectedAssessmentType}&`;
      if (selectedSection) url += `section_id=${selectedSection}`;
      
      const response = await axios.get(url, getAuthHeaders());
      setQuestions(response.data);
    } catch (err) {
      setError('Failed to load questions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadSections = async (assessmentTypeId) => {
    if (!assessmentTypeId) {
      setSections([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/assessment-types/${assessmentTypeId}/structure`, getAuthHeaders());
      // Flatten sections from hierarchical structure
      const allSections = [];
      response.data.domains.forEach(domain => {
        allSections.push({ id: domain.id, name: domain.name, code: domain.code });
        if (domain.categories) {
          domain.categories.forEach(cat => {
            allSections.push({ id: cat.id, name: `${domain.code} - ${cat.name}`, code: cat.code });
          });
        }
      });
      setSections(allSections);
    } catch (err) {
      console.error('Failed to load sections:', err);
    }
  };

  const handleCreateQuestion = async () => {
    if (!selectedAssessmentType || !selectedSection) {
      setError('Please select assessment type and section');
      return;
    }
    
    try {
      setLoading(true);
      const data = {
        ...questionForm,
        assessment_type_id: parseInt(selectedAssessmentType),
        section_id: parseInt(selectedSection)
      };
      await axios.post(`${API_URL}/questions`, data, getAuthHeaders());
      setSuccess('Question created successfully');
      setQuestionDialog({ open: false, question: null });
      setQuestionForm({
        question_text: '',
        guidance: '',
        subcategory_code: '',
        weight: 1.0,
        impact_reduction: 0,
        likelihood_reduction: 0,
        is_required: true
      });
      loadQuestions();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create question');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateQuestion = async () => {
    try {
      setLoading(true);
      await axios.put(`${API_URL}/questions/${questionDialog.question.id}`, questionForm, getAuthHeaders());
      setSuccess('Question updated successfully');
      setQuestionDialog({ open: false, question: null });
      setQuestionForm({
        question_text: '',
        guidance: '',
        subcategory_code: '',
        weight: 1.0,
        impact_reduction: 0,
        likelihood_reduction: 0,
        is_required: true
      });
      loadQuestions();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update question');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    try {
      setLoading(true);
      await axios.delete(`${API_URL}/questions/${questionId}`, getAuthHeaders());
      setSuccess('Question deleted successfully');
      loadQuestions();
    } catch (err) {
      setError('Failed to delete question');
    } finally {
      setLoading(false);
    }
  };

  const openQuestionDialog = (question = null) => {
    if (question) {
      setQuestionForm({
        question_text: question.question_text,
        guidance: question.guidance || '',
        subcategory_code: question.subcategory_code || '',
        weight: question.weight,
        impact_reduction: question.impact_reduction,
        likelihood_reduction: question.likelihood_reduction,
        is_required: question.is_required
      });
    } else {
      setQuestionForm({
        question_text: '',
        guidance: '',
        subcategory_code: '',
        weight: 1.0,
        impact_reduction: 0,
        likelihood_reduction: 0,
        is_required: true
      });
    }
    setQuestionDialog({ open: true, question });
  };

  // ==================== Effects ====================

  useEffect(() => {
    if (currentTab === 0) loadDashboard();
    else if (currentTab === 1) loadUsers();
    else if (currentTab === 2) loadAssessmentTypes();
    else if (currentTab === 3 && selectedAssessmentType) loadQuestions();
    else if (currentTab === 4) loadAllowedIps();
  }, [currentTab, selectedAssessmentType, selectedSection]);

  useEffect(() => {
    if (selectedAssessmentType) {
      loadSections(selectedAssessmentType);
      loadQuestions();
    }
  }, [selectedAssessmentType]);

  // ==================== Render ====================

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#2c3e50' }}>
        Admin Panel
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={(e, val) => setCurrentTab(val)}>
          <Tab icon={<DashboardIcon />} label="Dashboard" />
          <Tab icon={<PersonIcon />} label="Users" />
          <Tab icon={<AssessmentIcon />} label="Assessment Types" />
          <Tab icon={<QuestionIcon />} label="Questions" />
          <Tab icon={<LockIcon />} label="Security" />
        </Tabs>
      </Box>

      {/* ==================== DASHBOARD TAB ==================== */}
      {currentTab === 0 && (
        <Box>
          {loading ? (
            <CircularProgress />
          ) : stats ? (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h6">Total Users</Typography>
                    <Typography variant="h3">{stats.users}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h6">Total Clients</Typography>
                    <Typography variant="h3">{stats.clients}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h6">Assessment Types</Typography>
                    <Typography variant="h3">{stats.assessment_types}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h6">Total Assessments</Typography>
                    <Typography variant="h3">{stats.total_assessments}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h6">Completed</Typography>
                    <Typography variant="h3">{stats.completed_assessments}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h6">In Progress</Typography>
                    <Typography variant="h3">{stats.in_progress_assessments}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Typography>No stats available</Typography>
          )}

          {/* Quick Links */}
          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 'bold', color: '#2c3e50' }}>
            Quick Links
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card
                onClick={() => navigate('/admin/document-templates')}
                sx={{
                  cursor: 'pointer',
                  border: '2px solid #5c2d91',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                }}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <DescriptionIcon sx={{ fontSize: 36, color: '#5c2d91' }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#5c2d91' }}>
                      Document Templates
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manage DOCX templates for all client document types
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card
                onClick={() => navigate('/ai-gov/admin')}
                sx={{
                  cursor: 'pointer',
                  border: '2px solid #667eea',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                }}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AiIcon sx={{ fontSize: 36, color: '#667eea' }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#667eea' }}>
                      AI Governance Admin
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manage AI tools, policies, and templates
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* ==================== USERS TAB ==================== */}
      {currentTab === 1 && (
        <Box>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openUserDialog()}
              sx={{ bgcolor: '#3498db' }}
            >
              Create User
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadUsers}
            >
              Refresh
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#ecf0f1' }}>
                  <TableCell><strong>Username</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Full Name</strong></TableCell>
                  <TableCell><strong>Role</strong></TableCell>
                  <TableCell><strong>Auth Method</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.full_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.role}
                        color={user.role === 'admin' ? 'error' : 'primary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.auth_method || 'entra'}
                        color={user.auth_method === 'local' ? 'warning' : user.auth_method === 'both' ? 'secondary' : 'info'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? 'Active' : 'Inactive'}
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => openUserDialog(user)}>
                        <EditIcon />
                      </IconButton>
                      {user.is_active ? (
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeactivateUser(user.id)} 
                          color="error"
                          title="Deactivate User"
                        >
                          <DeleteIcon />
                        </IconButton>
                      ) : (
                        <IconButton 
                          size="small" 
                          onClick={() => handleReactivateUser(user.id)} 
                          color="success"
                          title="Reactivate User"
                        >
                          <RestoreIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ==================== ASSESSMENT TYPES TAB ==================== */}
      {currentTab === 2 && (
        <Box>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openTypeDialog()}
              sx={{ bgcolor: '#3498db' }}
            >
              Create Assessment Type
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadAssessmentTypes}
            >
              Refresh
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#ecf0f1' }}>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Code</strong></TableCell>
                  <TableCell><strong>Version</strong></TableCell>
                  <TableCell><strong>Questions</strong></TableCell>
                  <TableCell><strong>Assessments</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assessmentTypes.map((type) => (
                  <TableRow key={type.id}>
                    <TableCell>{type.name}</TableCell>
                    <TableCell>{type.code}</TableCell>
                    <TableCell>{type.version || '-'}</TableCell>
                    <TableCell>{type.question_count}</TableCell>
                    <TableCell>{type.assessment_count}</TableCell>
                    <TableCell>
                      <Chip
                        label={type.is_active ? 'Active' : 'Inactive'}
                        color={type.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => openTypeDialog(type)}>
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ==================== QUESTIONS TAB ==================== */}
      {currentTab === 3 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 300, flex: 1 }}>
              <InputLabel>Assessment Type</InputLabel>
              <Select
                value={selectedAssessmentType}
                onChange={(e) => {
                  setSelectedAssessmentType(e.target.value);
                  setSelectedSection('');
                }}
              >
                <MenuItem value="">Select Assessment Type...</MenuItem>
                {assessmentTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 300, flex: 1 }} disabled={!selectedAssessmentType}>
              <InputLabel>Section (Optional)</InputLabel>
              <Select
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
              >
                <MenuItem value="">All Sections</MenuItem>
                {sections.map((section) => (
                  <MenuItem key={section.id} value={section.id}>{section.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openQuestionDialog()}
              disabled={!selectedAssessmentType || !selectedSection}
              sx={{ bgcolor: '#3498db', minWidth: 200, height: '56px' }}
            >
              Create Question
            </Button>
          </Box>

          {selectedAssessmentType && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#ecf0f1' }}>
                    <TableCell width="35%"><strong>Question Text</strong></TableCell>
                    <TableCell><strong>Subcategory</strong></TableCell>
                    <TableCell><strong>Section</strong></TableCell>
                    <TableCell><strong>Weight</strong></TableCell>
                    <TableCell><strong>Impact</strong></TableCell>
                    <TableCell><strong>Likelihood</strong></TableCell>
                    <TableCell><strong>Required</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {questions.map((q) => (
                    <TableRow key={q.id}>
                      <TableCell>
                        {q.question_text.length > 100 
                          ? q.question_text.substring(0, 100) + '...' 
                          : q.question_text}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={q.subcategory_code || 'N/A'}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{q.section_name}</TableCell>
                      <TableCell>{q.weight}</TableCell>
                      <TableCell>{q.impact_reduction}%</TableCell>
                      <TableCell>{q.likelihood_reduction}%</TableCell>
                      <TableCell>
                        <Chip
                          label={q.is_required ? 'Yes' : 'No'}
                          color={q.is_required ? 'primary' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => openQuestionDialog(q)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteQuestion(q.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ==================== USER DIALOG ==================== */}
      <Dialog open={userDialog.open} onClose={() => setUserDialog({ open: false, user: null })} maxWidth="md" fullWidth>
        <DialogTitle>{userDialog.user ? 'Edit User' : 'Create User'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                autoFocus
                margin="dense"
                label="Username"
                fullWidth
                value={userForm.username}
                onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                disabled={!!userDialog.user}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Full Name"
                fullWidth
                value={userForm.full_name}
                onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Email"
                type="email"
                fullWidth
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label={userDialog.user ? 'New Password (leave blank to keep)' : 'Password'}
                type="password"
                fullWidth
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                helperText={userDialog.user ? 'Leave blank to keep current password' : 'Minimum 8 characters'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Role</InputLabel>
                <Select
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                >
                  <MenuItem value="assessor">Assessor</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Auth Method</InputLabel>
                <Select
                  value={userForm.auth_method}
                  onChange={(e) => setUserForm({ ...userForm, auth_method: e.target.value })}
                >
                  <MenuItem value="entra">Microsoft (Entra ID only)</MenuItem>
                  <MenuItem value="local">Local password only</MenuItem>
                  <MenuItem value="both">Both (Microsoft + local)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Module Permissions — hidden for admins (they always get full access) */}
          {userForm.role !== 'admin' && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Module Access
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell><strong>Module</strong></TableCell>
                      <TableCell><strong>Access Level</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {MODULES.map(({ key, label }) => (
                      <TableRow key={key}>
                        <TableCell>{label}</TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            value={modulePerms[key] ?? 'user'}
                            onChange={(e) => setModulePerms({ ...modulePerms, [key]: e.target.value })}
                            sx={{ minWidth: 140 }}
                          >
                            <MenuItem value="none">No Access</MenuItem>
                            <MenuItem value="user">User</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          {userForm.role === 'admin' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Admins have full access to all modules and bypass module permissions.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialog({ open: false, user: null })}>Cancel</Button>
          <Button
            onClick={userDialog.user ? handleUpdateUser : handleCreateUser}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : userDialog.user ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== SECURITY TAB ==================== */}
      {currentTab === 4 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 1, color: '#2c3e50' }}>
            Local Login — IP Allowlist
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            Local (username/password) login is only permitted from these IP addresses.
            All other logins must use Microsoft sign-in.
          </Typography>

          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openIpDialog()}
              sx={{ bgcolor: '#3498db' }}
            >
              Add IP Address
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadAllowedIps}>
              Refresh
            </Button>
          </Box>

          {allowedIps.length === 0 && !loading && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No IPs are allowlisted. Local login is currently blocked for all users.
              Add at least your office IP to enable admin fallback login.
            </Alert>
          )}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#ecf0f1' }}>
                  <TableCell><strong>IP Address</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Added</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allowedIps.map((ip) => (
                  <TableRow key={ip.id}>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {ip.ip_address}
                    </TableCell>
                    <TableCell>{ip.description || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={ip.is_active ? 'Active' : 'Disabled'}
                        color={ip.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(ip.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => openIpDialog(ip)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteIp(ip.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ==================== ASSESSMENT TYPE DIALOG ==================== */}
      <Dialog open={typeDialog.open} onClose={() => setTypeDialog({ open: false, type: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{typeDialog.type ? 'Edit Assessment Type' : 'Create Assessment Type'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={typeForm.name}
            onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Code"
            fullWidth
            value={typeForm.code}
            onChange={(e) => setTypeForm({ ...typeForm, code: e.target.value })}
            disabled={!!typeDialog.type}
          />
          <TextField
            margin="dense"
            label="Version"
            fullWidth
            value={typeForm.version}
            onChange={(e) => setTypeForm({ ...typeForm, version: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={typeForm.description}
            onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTypeDialog({ open: false, type: null })}>Cancel</Button>
          <Button 
            onClick={handleCreateAssessmentType}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== QUESTION DIALOG ==================== */}
      <Dialog open={questionDialog.open} onClose={() => setQuestionDialog({ open: false, question: null })} maxWidth="md" fullWidth>
        <DialogTitle>{questionDialog.question ? 'Edit Question' : 'Create Question'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Question Text"
            fullWidth
            multiline
            rows={3}
            value={questionForm.question_text}
            onChange={(e) => setQuestionForm({ ...questionForm, question_text: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Subcategory Code (e.g., ID.AM-1)"
            fullWidth
            value={questionForm.subcategory_code}
            onChange={(e) => setQuestionForm({ ...questionForm, subcategory_code: e.target.value })}
            placeholder="ID.AM-1"
          />
          <TextField
            margin="dense"
            label="Guidance"
            fullWidth
            multiline
            rows={2}
            value={questionForm.guidance}
            onChange={(e) => setQuestionForm({ ...questionForm, guidance: e.target.value })}
          />
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                margin="dense"
                label="Weight"
                type="number"
                fullWidth
                value={questionForm.weight}
                onChange={(e) => setQuestionForm({ ...questionForm, weight: parseFloat(e.target.value) })}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                margin="dense"
                label="Impact Reduction %"
                type="number"
                fullWidth
                value={questionForm.impact_reduction}
                onChange={(e) => setQuestionForm({ ...questionForm, impact_reduction: parseFloat(e.target.value) })}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                margin="dense"
                label="Likelihood Reduction %"
                type="number"
                fullWidth
                value={questionForm.likelihood_reduction}
                onChange={(e) => setQuestionForm({ ...questionForm, likelihood_reduction: parseFloat(e.target.value) })}
              />
            </Grid>
          </Grid>
          <FormControlLabel
            control={
              <Switch
                checked={questionForm.is_required}
                onChange={(e) => setQuestionForm({ ...questionForm, is_required: e.target.checked })}
              />
            }
            label="Required Question"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuestionDialog({ open: false, question: null })}>Cancel</Button>
          <Button 
            onClick={questionDialog.question ? handleUpdateQuestion : handleCreateQuestion}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : questionDialog.question ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* ==================== IP DIALOG ==================== */}
      <Dialog open={ipDialog.open} onClose={() => setIpDialog({ open: false, ip: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{ipDialog.ip ? 'Edit Allowed IP' : 'Add Allowed IP'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="IP Address"
            fullWidth
            value={ipForm.ip_address}
            onChange={(e) => setIpForm({ ...ipForm, ip_address: e.target.value })}
            disabled={!!ipDialog.ip}
            placeholder="e.g. 203.0.113.45"
            helperText="IPv4 or IPv6 address"
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            value={ipForm.description}
            onChange={(e) => setIpForm({ ...ipForm, description: e.target.value })}
            placeholder="e.g. Office, Home VPN, Mike laptop"
          />
          {ipDialog.ip && (
            <FormControlLabel
              control={
                <Switch
                  checked={ipForm.is_active}
                  onChange={(e) => setIpForm({ ...ipForm, is_active: e.target.checked })}
                />
              }
              label="Active"
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIpDialog({ open: false, ip: null })}>Cancel</Button>
          <Button
            onClick={ipDialog.ip ? handleUpdateIp : handleCreateIp}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : ipDialog.ip ? 'Update' : 'Add IP'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default AdminPanel;
